'use client';

import { c as _c } from "react/compiler-runtime";
import { getRecaptchaToken } from "@/utils/recaptcha";
import { appConfig } from '@/appConfig';
import { recordPrequalifyIntentAction, recordRouteOneHandoffAction } from '@/actions/prequalifyIntent';
import { useUtmParams } from '@/hooks/useUtmParams';
import type { IntentVehicleContext } from '@/types/prequalify';
import { buildRouteOneFinancingUrl } from '@/utils/routeOneFinancing';
interface OpenRouteOneFinancingOptions {
  analyticsAttemptId?: string;
  submissionId?: string;
  pageSection?: string;
  vehicle?: IntentVehicleContext;
  /**
   * Contact details from the pre-qualify gate. Without them the intent is
   * anonymous and the RouteOne lead that comes back can only be matched by
   * timing.
   */
  contact?: {
    firstName?: string;
    lastName?: string;
    name?: string;
    email?: string;
    phone?: string;
  };
}
function buildIntentVehicle(vehicle: IntentVehicleContext | undefined) {
  if (!vehicle?.vin) {
    return undefined;
  }
  return {
    vin: vehicle.vin,
    ...(vehicle.year !== undefined ? {
      year: vehicle.year
    } : {}),
    ...(vehicle.make !== undefined ? {
      make: vehicle.make
    } : {}),
    ...(vehicle.model !== undefined ? {
      model: vehicle.model
    } : {}),
    ...(vehicle.trim !== undefined ? {
      trim: vehicle.trim
    } : {}),
    ...(vehicle.price !== undefined ? {
      price: vehicle.price
    } : {}),
    ...(vehicle.dealerId !== undefined ? {
      dealerId: vehicle.dealerId
    } : {})
  };
}
export function useRouteOneFinancing(configuredUrl) {
  const $ = _c(9);
  const utmParams = useUtmParams();
  const dealerId = appConfig.DEALER_ID;
  let t0;
  if ($[0] !== configuredUrl || $[1] !== utmParams) {
    t0 = buildRouteOneFinancingUrl(configuredUrl, dealerId, utmParams);
    $[0] = configuredUrl;
    $[1] = utmParams;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const routeOneUrl = t0;
  const recordRouteOneIntent = _temp3;
  let t1;
  if ($[3] !== configuredUrl || $[4] !== utmParams) {
    t1 = async t2 => {
      const options = t2 === undefined ? {} : t2;
      const applicationWindow = window.open("about:blank", "_blank");
      if (applicationWindow) {
        applicationWindow.opener = null;
      }
      const submissionId_0 = options.submissionId ?? crypto.randomUUID();
      const result_0 = await recordRouteOneIntent({
        ...options,
        submissionId: submissionId_0
      });
      if (!result_0?.success || !result_0.id) {
        applicationWindow?.close();
        return {
          success: false as const,
          error: result_0?.error || "We could not save your details. Please try again."
        };
      }
      const prefilledUrl = buildRouteOneFinancingUrl(configuredUrl, dealerId, utmParams, options.contact);
      const recordHandoff = () => recordRouteOneHandoffAction(result_0.id, submissionId_0);
      if (!applicationWindow || applicationWindow.closed) {
        return {
          success: true as const,
          id: result_0.id,
          handoffUrl: prefilledUrl,
          recordHandoff
        };
      }
      try {
        applicationWindow.location.replace(prefilledUrl);
      } catch {
        applicationWindow.close();
        return {
          success: true as const,
          id: result_0.id,
          handoffUrl: prefilledUrl,
          recordHandoff
        };
      }
      await recordHandoff().catch(_temp4);
      return {
        success: true as const,
        id: result_0.id
      };
    };
    $[3] = configuredUrl;
    $[4] = utmParams;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const openRouteOneFinancing = t1;
  let t2;
  if ($[6] !== openRouteOneFinancing || $[7] !== routeOneUrl) {
    t2 = {
      routeOneUrl,
      recordRouteOneIntent,
      openRouteOneFinancing
    };
    $[6] = openRouteOneFinancing;
    $[7] = routeOneUrl;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
function _temp4() {
  return {
    success: false
  };
}
function _temp3(t0) {
  const {
    vehicle,
    contact,
    analyticsAttemptId,
    submissionId,
    pageSection
  } = t0 === undefined ? {} : t0;
  const intentVehicle = buildIntentVehicle(vehicle);
  return getRecaptchaToken("financing_intent").then(recaptchaToken => recordPrequalifyIntentAction({
    recaptchaToken,
    intentType: "route_one",
    ...(submissionId ? {
      submissionId
    } : {}),
    ...(pageSection ? {
      pageSection
    } : {}),
    ...(analyticsAttemptId ? {
      analyticsAttemptId
    } : {}),
    pageUrl: window.location.href,
    ...(intentVehicle ? {
      vehicle: intentVehicle
    } : {}),
    ...(contact ? {
      contact
    } : {})
  })).then(_temp).catch(_temp2);
}
function _temp2(error) {
  console.warn("Failed to record pre-qualify intent:", error);
}
function _temp(result) {
  if (!result.success) {
    console.warn("Failed to record pre-qualify intent:", result.error);
  }
  return result;
}